import { PlusIcon } from '@heroicons/react/24/outline';
import { useStytchUser } from '@stytch/nextjs';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import GenericModal from 'components/app-components/modals/GenericModal';
import ProfileCircle from 'components/generic-components/ProfileCircle';
import Button from 'components/app-components/ui/button';
import CopyableText from 'components/generic-components/copyableText';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
import { IRoleType, ISubRoleType } from 'context/user/user.type';
import { useTheraFernClient } from 'hooks/useFern';
import { usePermissions } from 'hooks/usePermissions';
import CheckMark from 'icons/CheckMark';
import { AuthRoutes, CommonRoutes } from 'routes';
import { downloadCompanyLogo } from 'services/companyApi';
import { ICompany, IEmployment } from 'types';
import {
  LS_key_Current_loggedIn_as_RoleType,
  LS_key_Current_loggedIn_as_SubRoleType,
  LS_key_Current_loggedIn_companyId,
  getAccountSwitchingOptions,
} from 'utils/affiliations';
import { isRecentRoute } from 'utils/app';
import { logError } from 'utils/error';
import { buildIntercomCompanies, updateIntercomSettings } from 'utils/intercom';
import { getInitials } from 'utils/string';
import { doWorkerActions, isValidRoute, showIDToCopy } from 'utils/user';
import { initialTheraRouteTimestamp_LS_key, initialTheraRoute_LS_key } from '../AuthRoutingHandler';

const CompanyAvatar = ({
  companyId,
  logoS3Key,
  companyInitials,
}: {
  companyId: string;
  logoS3Key?: string;
  companyInitials: string;
}) => {
  const [logoURL, setLogoURL] = useState('');

  useEffect(() => {
    if (!logoS3Key) return;

    let isMounted = true;
    (async () => {
      try {
        const response = await downloadCompanyLogo(companyId);
        if (isMounted) setLogoURL(response);
      } catch (error) {
        logError(error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [companyId, logoS3Key]);

  if (logoURL) {
    return <img src={logoURL} alt="" className="h-8 w-8 rounded-full object-cover" />;
  }

  return <ProfileCircle text={companyInitials} size="sm" id={companyInitials} />;
};

const SwitchAccountModal = ({ isOpen, onClose }) => {
  const router = useRouter();
  const { theraFernApi } = useTheraFernClient();
  const { user: stytchUser } = useStytchUser();
  const { isOrgAdmin, isHRSpecialist } = usePermissions();
  const [isCheckingCanCreateNewCompany, setIsCheckingCanCreateNewCompany] = useState(false);
  const {
    user,
    setUser,
    currentCompany,
    affiliationsByCompany,
    isLoading,
    isLoggingOut,
    roleType: currentUserRoleType,
    setRoleType,
    subRoleType: currentUserSubRoleType,
    setSubRoleType,
    setCurrentContract,
    setCurrentCompany,
    setCurrentContractId,
    setCurrentTeamId,
    setShowAccountSwitcher,
    logout,
    setIsAccountSwitchingInProgress,
    mutate: mutateUser,
  } = useUser();

  const showCompanyIDToCopy = useMemo(() => {
    return showIDToCopy(affiliationsByCompany);
  }, [affiliationsByCompany]);

  const handleOnClose = () => {
    // return if no current role
    if (!currentUserRoleType) {
      logout();
      return;
    }
    onClose();
  };

  const createAt = DateTime.fromISO(stytchUser?.created_at).toSeconds();
  const intercomValues = {
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    role: currentUserRoleType,
    user_id: user.userID,
    created_at: createAt,
  };

  const handleSignInAs = async (roleType: IRoleType, companyId: string, subRoleType?: ISubRoleType) => {
    const company = affiliationsByCompany[companyId].company;
    const hasInitialRole = Boolean(currentUserRoleType);
    // set current company and role type
    setCurrentCompany(company as unknown as ICompany);
    setRoleType(roleType);
    setSubRoleType(subRoleType);
    setIsAccountSwitchingInProgress(true);

    // set local storage
    try {
      window.localStorage.setItem(LS_key_Current_loggedIn_companyId, companyId);
      window.localStorage.setItem(LS_key_Current_loggedIn_as_RoleType, roleType);
      if (subRoleType) {
        window.localStorage.setItem(LS_key_Current_loggedIn_as_SubRoleType, subRoleType);
      } else {
        window.localStorage.removeItem(LS_key_Current_loggedIn_as_SubRoleType);
      }
      // if has initial role, redirect to dashboard because worker shouldn't see admin routes and vice versa
      if (hasInitialRole) {
        return window.location.replace(CommonRoutes.Dashboard);
      }
    } catch (error) {
      logError(error);
    }

    if (roleType === 'worker') {
      // load worker first contract
      let firstContract;
      if (subRoleType === 'w2Employee') {
        firstContract = affiliationsByCompany[companyId]?.w2Contracts?.[0];
      } else if (subRoleType === 'contractor') {
        firstContract = affiliationsByCompany[companyId]?.contractorContracts?.[0];
      } else if (subRoleType === 'eorEmployee') {
        firstContract = affiliationsByCompany[companyId]?.eorContracts?.[0];
      } else {
        firstContract = affiliationsByCompany[companyId]?.contracts?.[0];
      }
      setCurrentContract(firstContract as unknown as IEmployment);
      setCurrentContractId(firstContract.id);

      const { updatedUser, routeToRedirect } = await doWorkerActions({
        user,
        contract: firstContract as unknown as IEmployment,
      });

      const _updatedUser = {
        ...user,
        ...updatedUser,
      };
      setUser(_updatedUser);

      if (routeToRedirect) {
        setShowAccountSwitcher(false);
        await router.replace(routeToRedirect);
        return;
      }
    } else if (roleType === 'manager') {
      // if manager, set roleType to manager, set "all teams" as the current team
      setCurrentTeamId(null);
    } else {
      logError(new Error('Invalid role type'));
    }
    updateIntercomSettings({
      ...intercomValues,
      role: roleType,
      companies: buildIntercomCompanies(affiliationsByCompany),
    });
    if (hasInitialRole) {
      window.location.replace(CommonRoutes.Dashboard);
    } else {
      // check if local storage has initial route
      const initialRoute = localStorage?.getItem(initialTheraRoute_LS_key);
      const initialRouteTimestamp = localStorage?.getItem(initialTheraRouteTimestamp_LS_key);
      if (initialRoute && isRecentRoute(initialRouteTimestamp)) {
        // remove initial route from local storage
        localStorage.removeItem(initialTheraRoute_LS_key);
        localStorage.removeItem(initialTheraRouteTimestamp_LS_key);
        if (isValidRoute(initialRoute, roleType)) {
          await router.replace(initialRoute);
        } else {
          await router.replace(CommonRoutes.Dashboard);
        }
      } else {
        await router.replace(CommonRoutes.Dashboard);
      }
    }
    setShowAccountSwitcher(false);
    setIsAccountSwitchingInProgress(false);
    await mutateUser();
    onClose();
  };

  const checkCanCreateNewCompany = useCallback(async () => {
    if (isOrgAdmin || isHRSpecialist) {
      setIsCheckingCanCreateNewCompany(false);
      return true;
    }
    // check if the current user (worker) is on the allowed list.
    try {
      setIsCheckingCanCreateNewCompany(true);
      const authorizationResponse = await theraFernApi.thera.company.companyApi.checkCanCreateAccount({
        email: user?.email,
        onlyCheckAllowlist: true,
      });
      return authorizationResponse?.authorized ?? false;
    } catch (error) {
      logError(error);
      return false;
    } finally {
      setIsCheckingCanCreateNewCompany(false);
    }
  }, [isOrgAdmin, isHRSpecialist, user?.email, theraFernApi]);

  const addNewCompany = async () => {
    const canCreateNewCompany = await checkCanCreateNewCompany();
    if (!canCreateNewCompany) {
      theraToast.warning('You are not authorized to create a new company. Please contact support@getthera.com.', {
        id: 'not-authorized-to-create-new-company',
      });
      return;
    }
    router.push(`${AuthRoutes.SignUpOnboarding}?newCompany=true`);
  };

  if (isLoading || isLoggingOut || !affiliationsByCompany) return null;

  return (
    <GenericModal
      open={isOpen}
      onClose={handleOnClose}
      title="Select Account"
      showHeaderBorder={false}
      width="md:min-w-lg min-w-xs"
    >
      <div className="space-y-2">
        {Object.keys(affiliationsByCompany).map((companyId) => {
          const companyName = affiliationsByCompany[companyId].company.companyName;
          const dbaName = affiliationsByCompany[companyId].company.dbaName;
          const logoS3Key = affiliationsByCompany[companyId].company.logoS3Key;
          const companyInitials = getInitials(companyName);
          const isCurrentCompany = currentCompany?.companyID === companyId;
          const currentlySignedInRoleType = currentUserRoleType;
          const affiliation = affiliationsByCompany[companyId];
          const switchingOptions = getAccountSwitchingOptions(affiliation);
          const isCompanyAndDBANameSame = companyName === dbaName;

          return (
            <div key={companyId} className="rounded-md border border-neutral-100">
              <div className="flex w-full items-center justify-between space-x-3">
                <div className="flex items-center space-x-3 p-4">
                  <CompanyAvatar companyId={companyId} logoS3Key={logoS3Key} companyInitials={companyInitials} />
                  <div className="flex flex-col gap-0.5">
                    <p className="text-base font-medium text-neutral-900">{companyName}</p>
                    {!isCompanyAndDBANameSame && <p className="text-xs font-normal text-neutral-500">{dbaName}</p>}
                  </div>
                  {showCompanyIDToCopy && (
                    <CopyableText text={companyId} textClassName="text-xs font-medium text-neutral-500 mt-1" />
                  )}
                </div>
              </div>
              {switchingOptions.length > 0 &&
                switchingOptions.map((option) => {
                  // if current role at current company, don't show button
                  const isSignedInAs =
                    isCurrentCompany &&
                    option.type === currentlySignedInRoleType &&
                    (currentlySignedInRoleType === 'worker' ? option?.subRoleType === currentUserSubRoleType : true);
                  if (isSignedInAs) {
                    return (
                      <div
                        key={`${option.type}-${option.subRoleType}`}
                        className={`flex items-center justify-between space-x-1 px-4 py-3 ${
                          isSignedInAs ? 'bg-neutral-100' : ''
                        }`}
                      >
                        <span className="text-sm font-medium text-neutral-600">Signed in as {option.label}</span>
                        <div className="flex items-center space-x-1">
                          <CheckMark fill="blue" className="h-5 w-5" />
                          <span className="text-xs font-medium text-neutral-900">Signed in</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <Button
                      variant="ghost"
                      key={`${option.type}-${option.subRoleType}`}
                      className={`flex h-auto w-full items-center justify-between space-x-1 rounded-none border-t border-neutral-100 px-4 py-3 hover:bg-neutral-50 ${
                        isSignedInAs ? 'bg-neutral-100' : ''
                      }`}
                      onClick={() => handleSignInAs(option.type, companyId, option?.subRoleType)}
                    >
                      <span className="text-sm font-medium text-therablue underline">Sign in as {option.label}</span>
                    </Button>
                  );
                })}
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <Button
          onClick={addNewCompany}
          disabled={isCheckingCanCreateNewCompany}
          loading={isCheckingCanCreateNewCompany}
          loaderStrokeColor="#1a1a1a"
          className="flex w-full !justify-start !border-none !bg-neutral-100 hover:!bg-neutral-200"
        >
          <PlusIcon className="mr-2 h-5 w-auto rounded-full bg-white text-black" />
          <span className="text-sm font-medium text-neutral-900">Add New Company</span>
        </Button>
      </div>
      {!currentUserRoleType && (
        <div className="mt-3">
          <Button variant="plain" onClick={() => logout()}>
            <span className="text-xs text-therablue underline">Logout</span>
          </Button>
        </div>
      )}
    </GenericModal>
  );
};

export default SwitchAccountModal;
